import {
  AnalyticsBrowser,
  type InitOptions,
} from '@customerio/cdp-analytics-browser'
import { events, sourceIDs } from 'analytics/events'
import { track } from './analytics'
import { getEnvVar } from './env'
import { navigateTo } from './navigationService'

const CUSTOMERIO_WRITE_KEY = getEnvVar('CUSTOMERIO_WRITE_KEY')

const cioanalytics = new AnalyticsBrowser()

type CIOLoadStatus = 'disabled' | 'loading' | 'loaded' | 'failed'

let cioLoadStatus: CIOLoadStatus = 'disabled'

function canSendCIOCalls() {
  return cioLoadStatus === 'loading' || cioLoadStatus === 'loaded'
}

export function handleCIOEvent(e: Event) {
  const { actionValue, message } = (e as CustomEvent).detail

  switch (e.type) {
    case 'in-app:message-action':
      if (
        actionValue.startsWith('/') ||
        actionValue.includes('my.tovala.com') ||
        actionValue.includes('m.tvla.co')
      ) {
        const url = new URL(actionValue, window.location.origin)
        let path = url.pathname
        const searchParams = url.searchParams

        if (path.startsWith('/t/')) {
          const termID = getDeeplinkID(path)
          if (termID) {
            const tabParam = searchParams.get('tab')
            // Meals tab is the default, so we don't need to add it to the path
            const validTabs = ['extras']
            const tab =
              !tabParam || !validTabs.includes(tabParam) ? '' : tabParam
            path = `/menu/${termID}${tab ? `?tab=${tab}` : ''}`
          } else {
            path = '/menu'
          }
        } else if (path.startsWith('/m/')) {
          const mealID = getDeeplinkID(path)
          if (mealID) {
            path = `/my-orders?meal=${mealID}`
          } else {
            path = '/my-orders'
          }
        } else if (path.startsWith('/p/')) {
          path = `/my-orders${getProductQuery(path, searchParams)}`
        }

        track(events.DEEPLINK_TRIGGERED, {
          source_id: sourceIDs.INLINE_MESSAGE,
          url: new URL(path, window.location.origin).toString(),
        })
        navigateTo(path)
        message.dismiss() // Close the in-app message
      }
      break
    default:
      break
  }
}

export function initCIOAnalytics() {
  if (getEnvVar('APP_ENV') !== 'test') {
    cioLoadStatus = 'loading'
    cioanalytics.then(
      () => {
        cioLoadStatus = 'loaded'
        syncCIOInAppRoute()
      },
      () => {
        cioLoadStatus = 'failed'
      },
    )
    cioanalytics.load(
      {
        writeKey: CUSTOMERIO_WRITE_KEY,
      },
      {
        integrations: {
          'Customer.io In-App Plugin': {
            events: handleCIOEvent,
          },
        },
      } as unknown as InitOptions,
    )
  }
}

const IN_APP_PLUGIN_ONLY_OPTIONS = {
  integrations: { All: false, 'Customer.io Data Pipelines': false },
}

export function identifyCIOUser(userID: number) {
  if (!canSendCIOCalls()) {
    return
  }

  cioanalytics
    .identify(`${userID}`, {}, IN_APP_PLUGIN_ONLY_OPTIONS)
    .catch(() => undefined)
}

export function resetCIOUser() {
  if (!canSendCIOCalls()) {
    return
  }

  cioanalytics.reset().catch(() => undefined)
}

let isInAppRouteSyncScheduled = false

export function syncCIOInAppRoute() {
  if (cioLoadStatus !== 'loaded' || isInAppRouteSyncScheduled) {
    return
  }

  isInAppRouteSyncScheduled = true
  queueMicrotask(() => {
    isInAppRouteSyncScheduled = false
    cioanalytics
      .page(window.location.pathname, {}, IN_APP_PLUGIN_ONLY_OPTIONS)
      .catch(() => undefined)
  })
}

function getDeeplinkID(path: string) {
  return path.split('/')[2]
}

function getProductQuery(path: string, searchParams: URLSearchParams) {
  const productID = getDeeplinkID(path)
  const termID = searchParams.get('t')

  if (!productID || !termID) {
    return ''
  }

  return `?${new URLSearchParams({ extra: productID, menu: termID })}`
}
